import { Computer, Gamepad2, Crown, Star } from "lucide-react";
import type { AvatarConfig } from '@/lib/avatar-config';

export interface BotData {
  name: string;
  elo: number;
  icon: React.ComponentType<any>;
  color: string;
  description: string;
  avatarConfig: AvatarConfig;
  personality: string;
  playStyle: string;
}

export const computerOpponents: BotData[] = [
  { 
    name: "Marty", 
    elo: 400, 
    icon: Computer, 
    color: "text-teen-green", 
    description: "Just getting started! 🌱",
    avatarConfig: {
      category: 'animal',
      style: 'rabbit',
      colors: { primary: '#10b981', secondary: '#06b6d4', accent: '#34d399' },
      features: {},
      animations: { idle: 'bounce', celebration: 'jump', thinking: 'wiggle' }
    },
    personality: "Friendly beginner - makes lots of mistakes but always smiling!",
    playStyle: "Random moves, overlooks tactics, leaves pieces hanging"
  },
  { 
    name: "Sophie", 
    elo: 700, 
    icon: Gamepad2, 
    color: "text-teen-cyan", 
    description: "Learning every day! 📚",
    avatarConfig: {
      category: 'human-female',
      style: 'gamer',
      colors: { primary: '#06b6d4', secondary: '#3b82f6', accent: '#22d3ee' },
      features: {},
      animations: { idle: 'float', celebration: 'spin', thinking: 'bounce' }
    },
    personality: "Eager learner - improving every game!",
    playStyle: "Knows basic tactics, sometimes misses simple combinations"
  },
  { 
    name: "Carlos", 
    elo: 900, 
    icon: Computer, 
    color: "text-teen-blue", 
    description: "Chill vibes, solid moves 🎮",
    avatarConfig: {
      category: 'animal',
      style: 'fox',
      colors: { primary: '#3b82f6', secondary: '#8b5cf6', accent: '#60a5fa' },
      features: {},
      animations: { idle: 'sway', celebration: 'dance', thinking: 'nod' }
    },
    personality: "Laid-back strategist - takes his time, plays solid.",
    playStyle: "Sound positional play, occasional tactical oversights"
  },
  { 
    name: "Nina", 
    elo: 1200, 
    icon: Crown, 
    color: "text-teen-orange", 
    description: "Club champion material! ♟️",
    avatarConfig: {
      category: 'fantasy',
      style: 'knight',
      colors: { primary: '#f97316', secondary: '#fbbf24', accent: '#fb923c' },
      features: {},
      animations: { idle: 'stand', celebration: 'victory', thinking: 'strategize' }
    },
    personality: "Competitive club player - knows her openings!",
    playStyle: "Strong opening preparation, aggressive middlegame play"
  },
  { 
    name: "Jason", 
    elo: 1400, 
    icon: Computer, 
    color: "text-teen-purple", 
    description: "Tactical mastermind 👑",
    avatarConfig: {
      category: 'object',
      style: 'lightning',
      colors: { primary: '#8b5cf6', secondary: '#ec4899', accent: '#a855f7' },
      features: {},
      animations: { idle: 'pulse', celebration: 'flash', thinking: 'spark' }
    },
    personality: "Tactical wizard - always looking for combinations!",
    playStyle: "Sharp tactics, seeks complications, sacrifices for attack"
  },
  { 
    name: "Aisha", 
    elo: 2000, 
    icon: Star, 
    color: "text-teen-pink", 
    description: "Expert-level precision ⚡",
    avatarConfig: {
      category: 'fantasy',
      style: 'mage',
      colors: { primary: '#ec4899', secondary: '#8b5cf6', accent: '#f472b6' },
      features: {},
      animations: { idle: 'levitate', celebration: 'conjure', thinking: 'meditate' }
    },
    personality: "Calculating master - precise and methodical.",
    playStyle: "Deep calculation, strategic planning, rare mistakes"
  },
  { 
    name: "Viktor", 
    elo: 2800, 
    icon: Crown, 
    color: "text-teen-gold", 
    description: "Fear the grandmaster! ⭐",
    avatarConfig: {
      category: 'object',
      style: 'crown',
      colors: { primary: '#fbbf24', secondary: '#f97316', accent: '#fcd34d' },
      features: {},
      animations: { idle: 'shine', celebration: 'glow', thinking: 'radiate' }
    },
    personality: "Ruthless grandmaster - crushes without mercy!",
    playStyle: "Grandmaster-level precision, exploits every weakness"
  },
  { 
    name: "Magnus", 
    elo: 3200, 
    icon: Star, 
    color: "text-teen-red", 
    description: "World-class strength! 🏆",
    avatarConfig: {
      category: 'fantasy',
      style: 'dragon',
      colors: { primary: '#ef4444', secondary: '#fbbf24', accent: '#f87171' },
      features: {},
      animations: { idle: 'breathe', celebration: 'roar', thinking: 'focus' }
    },
    personality: "World champion strength - the ultimate challenge!",
    playStyle: "Perfect play, no weaknesses, near-impossible to defeat"
  },
];

// Debug logging to verify bot data
console.log('🤖 Bot Constants Debug:', computerOpponents.map(bot => ({ name: bot.name, elo: bot.elo })));

export const getBotNameByElo = (elo: number): string => {
  const bot = computerOpponents.find(b => b.elo === elo);
  return bot?.name || `Bot ${elo}`;
};

export const getBotByName = (name: string): BotData | undefined => {
  return computerOpponents.find(b => b.name === name);
};

export const getBotByElo = (elo: number): BotData | undefined => {
  return computerOpponents.find(b => b.elo === elo);
};

export const getBotAvatarConfig = (botName: string): AvatarConfig | undefined => {
  return getBotByName(botName)?.avatarConfig;
};

export const getBotSkillLevel = (elo: number): string => {
  console.log(`🎯 Getting skill level for ELO: ${elo}`);
  let skillLevel: string;
  if (elo < 600) skillLevel = "Beginner";
  else if (elo < 1000) skillLevel = "Intermediate";
  else if (elo < 1600) skillLevel = "Advanced";
  else skillLevel = "Expert";
  
  console.log(`🎯 ELO ${elo} -> Skill Level: ${skillLevel}`);
  return skillLevel;
};